import { decorate, observable, action, computed } from "mobx";
import _ from "lodash";
import {
  ALL_TEST_GROUPS,
  EXPERIENCE_ROLES,
  TEST_GROUP_ID_MAPPING,
  USER_PERMISSIONS
} from "../utils/authConstants";

class AuthStore {
  isAuthenticated = null;
  isGettingUser = false;
  accessToken = null;
  user = {};

  constructor(auth) {
    this.auth = auth;
  }

  getUser = async () => {
    this.isGettingUser = true;
    try {
      await this.checkAuthentication();
      if (this.isAuthenticated) {
        this.user = await this.auth.getUser();
      }
      this.isGettingUser = false;
    } catch (e) {
      //Something went wrong, clear out localStorage to make the user login again
      window.localStorage.clear();
      window.location.reload();
    }
  };

  checkAuthentication = async () => {
    //isAuthenticated only requires an accessToken, we need the idToken for getting the user's information
    const idToken = await this.auth._oktaAuth.tokenManager.get("idToken");
    const accessToken = await this.auth._oktaAuth.tokenManager.get(
      "accessToken"
    );

    const isAuthenticated =
      (await this.auth.isAuthenticated()) && idToken !== undefined;
    if (isAuthenticated !== this.isAuthenticated) {
      this.isAuthenticated = isAuthenticated;
    }

    if (accessToken) {
      this.accessToken = accessToken.accessToken;
    }
  };

  onSuccess = async res => {
    if (res.status === "SUCCESS") {
      /**
       * this.auth.redirect sets the tokens in localStorage.
       * However the timing is unpredictable and can be too slow for initial api requests
       * getWithoutPrompt allows us to guarantee that the tokens are available on initial login.
       */
      const tokens = await this.auth._oktaAuth.token.getWithoutPrompt({
        sessionToken: res.session.token,
        responseType: ["token", "id_token"],
        scopes: ["openid", "email", "profile"]
      });
      const [accessToken, idToken] = tokens;
      const tokenString = JSON.stringify({ accessToken, idToken });
      window.localStorage.setItem("okta-token-storage", tokenString);
      return this.auth.redirect({
        sessionToken: res.session.token
      });
    } else {
      // The user can be in another authentication state that requires further action.
      // For more information about these states, see:
      //   https://github.com/okta/okta-signin-widget#rendereloptions-success-error
    }
  };

  onError = err => {
    console.log("error logging in", err);
  };

  logout = () => {
    this.isAuthenticated = false;
    this.auth.logout("/login");
  };

  get canSendToForge() {
    return this.checkPermission(USER_PERMISSIONS.CAN_SEND_TO_FORGE);
  }

  get canViewAllRules() {
    return this.checkPermission(USER_PERMISSIONS.CAN_VIEW_ALL_RULES);
  }

  get canAccessResearchTool() {
    return this.checkPermission(USER_PERMISSIONS.CAN_ACCESS_RESEARCH_TOOL);
  }

  get canDownloadVideos() {
    return this.checkPermission(USER_PERMISSIONS.CAN_DOWNLOAD_VIDEOS);
  }

  get canAccessAdhocClipping() {
    return this.checkPermission(USER_PERMISSIONS.CAN_ACCESS_ADHOC_CLIPPING);
  }

  get canViewAllCustomClips() {
    return this.checkPermission(USER_PERMISSIONS.CAN_VIEW_ALL_CUSTOM_CLIPS);
  }

  get allowedExperiences() {
    return Object.keys(EXPERIENCE_ROLES).filter(experience =>
      this.checkPermission(EXPERIENCE_ROLES[experience])
    );
  }

  get testGroupId() {
    const userTestGroup = _.intersection(this.user.groups, ALL_TEST_GROUPS)[0];
    return TEST_GROUP_ID_MAPPING[userTestGroup];
  }

  get canAccessRules() {
    return this.checkPermission(USER_PERMISSIONS.CAN_ACCESS_RULES);
  }

  checkPermission(requestedPermission) {
    return _.intersection(this.user.groups, requestedPermission).length > 0;
  }
}

decorate(AuthStore, {
  isAuthenticated: observable,
  user: observable,
  isGettingUser: observable,
  getUser: action,
  canSendToForge: computed,
  canViewAllRules: computed,
  canAccessResearchTool: computed,
  canDownloadVideos: computed,
  canAccessAdhocClipping: computed,
  canViewAllCustomClips: computed,
  allowedExperiences: computed,
  canAccessRules: computed,
  testGroupId: computed,
  accessToken: observable
});

export default AuthStore;



// WEBPACK FOOTER //
// ./src/stores/AuthStore.js